import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, Subject, BehaviorSubject } from 'rxjs';
import { tap, map } from 'rxjs/operators';
import html2canvas from 'html2canvas';
import * as FileSaver from 'file-saver';

import { environment } from 'environments/environment';

import { Report } from '@classes/report/report';
import { FileInfo } from '@classes/file/file-info';
import { ReportTypeCode } from '@enums/report-type-code.enum';
import { Simulation } from '@classes/simulation/simulation';
import { ExportType } from '@enums/export-type.enum';
import { ReportCustom } from '@classes/report/report-custom';
import { Invest } from '@classes/invest/invest';
import { ProviderType } from '@enums/provider-type.enum';
import { FileService } from './file.service';
import { UiService } from './ui.service';
import { ReportData } from '@classes/report/report-data';
import { ReportItem } from '@classes/report/report-item';
import { ReportCode } from '@classes/report/report-code';

@Injectable({
    providedIn: 'root'
})
export class ReportService {
    selectedReportChanged = new Subject<Report>();
    printModeChanged = new Subject<boolean>();
    customReportsSubject = new BehaviorSubject<ReportCustom[]>([]);
    reportDataSubject = new BehaviorSubject<ReportData>(null);
    reportCracYearsSubject = new BehaviorSubject<ReportData>(null);

    private baseUrl = environment.apiUrl + 'reports';
    private report   : Report;
    private reportPPT: Report;

    constructor(
        private http: HttpClient,
        private uiSrv: UiService,
        private router: Router,
        private fileSrv: FileService
    ) { }

    getBySimulation(simulationsCodes: string[], reportCode = 'default', year: number = null): Observable<Report> {
        let httpParams = new HttpParams()
            .append("reportCode", reportCode);
        simulationsCodes.map(code => {
            httpParams = httpParams.append("simulationCodes", code);
        });
        if (year) {
            httpParams = httpParams.append("year", year.toString());
        }

        return this.http.get<Report>(`${this.baseUrl}/simulations`, { params: httpParams })
        .pipe(
            tap(reportFromApi => {
                this.report = reportFromApi;
                this.customReportsSubject.next([new ReportCustom(0)]);
                this.selectedReportChanged.next(reportFromApi);
            })
        );
    }

    getByGroup(groupsCodes: string[], year: number = null): Observable<Report> {
        let httpParams = new HttpParams();
        groupsCodes.map(code => {
            httpParams = httpParams.append("groupCodes", code);
        });
        if (year) {
            httpParams = httpParams.append("year", year.toString());
        }

        // return this.http.get<Report>("/assets/mock-data/mock-compare-with-table.json")
        return this.http.get<Report>(`${this.baseUrl}/compare`, { params: httpParams })
        .pipe(
            tap(reportFromApi => {
                this.report = reportFromApi;
                this.customReportsSubject.next([new ReportCustom(0)]);
                this.selectedReportChanged.next(reportFromApi);
            })
        );
    }

    getGroupsData(groupsCodes: string[], year: number = null): Observable<ReportData> {
        let httpParams = new HttpParams();
        groupsCodes.map(code => {
            httpParams = httpParams.append("groupCodes", code);
        });
        if (year) {
            httpParams = httpParams.append("year", year.toString());
        }

        // return this.http.get<ReportData>("/assets/mock-data/mock-compare-custom.json")
        return this.http.get<ReportData>(`${this.baseUrl}/compare-custom`, { params: httpParams })
        .pipe(
            tap(reportData => {
                this.reportDataSubject.next(reportData);
            })
        );
    }

    getCracYears(): Observable<ReportData> {
        return this.http.get<ReportData>(`${this.baseUrl}/crac/years`)
        .pipe(
            tap(reportData => {
                this.reportCracYearsSubject.next(reportData);
            })
        );
    }

    addCustomReport(): void {
        const customReports = this.customReportsSubject.value;
        const lastReport = customReports[customReports.length - 1];
        const newId = lastReport ? lastReport.id + 1 : 0;

        this.customReportsSubject.next([ ...customReports, new ReportCustom(newId) ]);
    }

    editCustomReport(report: ReportCustom): void {
        let customReports = this.customReportsSubject.value;

        customReports = customReports.map(r => r.id === report.id ? report : r);
        this.customReportsSubject.next([ ...customReports ]);
    }

    removeCustomReport(index: number): void {
        const customReports = this.customReportsSubject.value;

        customReports.splice(index, 1);

        this.customReportsSubject.next([ ...customReports ]);
    }

    generatePpt(): Observable<Blob> {
        return this.http.post(`${this.baseUrl}/ppt`, this.reportPPT, {observe: 'response', responseType: 'blob' })
        .pipe(map(res => this.saveFile(res)));
    }

    generateXlsFromSimu(simulation: Simulation): Observable<Blob> {
        return this.http.post(`${this.baseUrl}/xls/simulation`, simulation, {observe: 'response', responseType: 'blob' })
        .pipe(map(res => this.saveFile(res)));
    }

    generateXlsFromInvest(invest: Invest): Observable<Blob> {
        let url = `${this.baseUrl}/xls/investment`;

        if(invest.simulation.provider === ProviderType.bbc) {
            url += '/bbc';
        } else {
            url += '/sic';
        }

        return this.http.post(url, invest, {observe: 'response', responseType: 'blob' })
        .pipe(map(res => this.saveFile(res)));
    }

    generateXlsArticles(): Observable<Blob> {
        return this.http.get(`${this.baseUrl}/xls/articles`, {observe: 'response', responseType: 'blob' })
        .pipe(map(res => this.saveFile(res)));
    }

    generateXlsPartitions(partitionCodes: string[], partitioningCode: string, doesIncludeArticles: boolean, categoryCodes: string[]): Observable<Blob> {
        const postBody = {
            partitionCodes: partitionCodes,
            partitioningCode: partitioningCode,
            includeArticles: doesIncludeArticles,
            amountTypeCodes: categoryCodes
        };

        return this.http.post(`${this.baseUrl}/xls/partitions`, postBody, {observe: 'response', responseType: 'blob' })
        .pipe(map(res => this.saveFile(res)));
    }

    generateXlsLicences(code?: string): Observable<Blob> {
        let url = `${this.baseUrl}/xls/licences`;
        if(code) {
            url += `/${code}`;
        }

        return this.http.get(url, {observe: 'response', responseType: 'blob' })
        .pipe(map(res => this.saveFile(res)));
    }

    getXlsSystemCodes(): Observable<ReportCode[]> {
        return this.http.get<ReportCode[]>(`${this.baseUrl}/xls/system-codes`);
    }
    generateXlsSystem(report: ReportCode): Observable<Blob> {
        const parameter = report.parameters.map(p => p.selectedValue.code).join(',');
        const url = `${this.baseUrl}/xls/system/${report.code}?parameter=${parameter}`;

        return this.http
            .get(url, {observe: 'response', responseType: 'blob' })
            .pipe(map(res => this.saveFile(res)));
    }

    generateXlsBilling(year: number, month: string): Observable<Blob> {
        let url = `${this.baseUrl}/xls/billing/default/${year}/${month}`;

        return this.http.get(url, {observe: 'response', responseType: 'blob' })
        .pipe(map(res => this.saveFile(res)));
    }
    generateXlsBillingDebit(year: number, month: string): Observable<Blob> {
        let url = `${this.baseUrl}/xls/billing/debit/${year}/${month}`;

        return this.http.get(url, {observe: 'response', responseType: 'blob' })
        .pipe(map(res => this.saveFile(res)));
    }
    generateXlsRetrocession(year: number, month: string): Observable<Blob> {
        let url = `${this.baseUrl}/xls/billing/retrocession/${year}/${month}`;

        return this.http.get(url, {observe: 'response', responseType: 'blob' })
        .pipe(map(res => this.saveFile(res)));
    }

    generateXlsFromCode(simulationCode: string, exportType: ExportType, year: number): Observable<Blob> {
        let url = `${this.baseUrl}/xls/`;

        switch(exportType) {
            case ExportType.crac:
                url += 'crac';
                break;
            case ExportType.bxl:
                url += 'bxl';
                break;
        }

        const httpParams = new HttpParams().append('year', year.toString());

        return this.http.get(`${url}/${simulationCode}`, {params: httpParams, observe: 'response',  responseType: 'blob' })
        .pipe(map(res => this.saveFile(res)));
    }

    exportReportImages(): void {
        this.reportPPT = this.createReportPPT(this.report);
        this.mergeCustomsToReport();

        let customGroup = 0;
        let customIndex = 0;

        // send all images to api before report generation
        const printPromises = this.reportPPT.reportItems.map(item => {
            let id = item.selectedVariationCode;

            if(item.reportType.code === ReportTypeCode.custom) {
                const group = (item.group - item.group % 1000);
                if (group > customGroup)
                {
                    customGroup = group;
                    customIndex = 0;
                }
                id = `custom-${item.group}-${customIndex}`;
                ++customIndex;
            }

            return this.uploadReportImage(id);
        });

        // When all images are sent, add fileInfo on each reportItem and send them to generate ppt
        Promise.all(printPromises)
        .then((results: FileInfo[]) => {
            this.reportPPT.reportItems.map((item, i) => {
                item.file = results[i];
            });

            this.generatePpt().subscribe(() => this.printModeChanged.next(false));

        }, (err) => {
            this.printModeChanged.next(false);
            this.uiSrv.showSnackbar(err);
            this.router.navigate(['/error']);
        });
    }

    private saveFile(res: HttpResponse<Blob>): Blob {
        FileSaver.saveAs(res.body, res.headers.get('filename'));
        return res.body;
    }

    private mergeCustomsToReport(): void {
        // First clean up old custom itemps
        this.reportPPT.reportItems = this.reportPPT.reportItems
        .filter(r => r.reportType.code !== ReportTypeCode.custom);

        this.customReportsSubject.value
        .filter(r => r.reportItems && r.reportItems.length)
        .map(r => {
                this.reportPPT.reportItems.push( ...r.reportItems );
        });
    }

    private uploadReportImage(id: string): Promise<FileInfo> {
        return new Promise((resolve, reject) => {
            const query = document.querySelector(`#print-view #${id}`);

            if(!query) {
                return reject(new Error(`Chart not found`));
            }

            html2canvas(query, {logging: false})
            .then((canvas: HTMLCanvasElement) => {
                canvas.toBlob(blob => {
                    this.fileSrv.uploadSimple(blob)
                    .subscribe(fileInfo => {
                        return resolve(fileInfo);
                    });
                });
            }, () => {
                return reject(new Error(`Image creation failed`));
            });
        });
    }

    private createReportPPT (report: Report): Report
    {
        return {
            code       : report.code
        ,   username   : report.username
        ,   createdAt  : report.createdAt
        ,   text       : report.text
        ,   reportItems: this.createReportItemsPPT(report.reportItems)
        };
    }
    private createReportItemsPPT (items: ReportItem[]): ReportItem[]
    {
        const ppt: ReportItem[] = [];

        items.forEach((item, index) => {
            item.group = index;
            item.variations
                .sort((a, b) => a.code <= b.code ? -1 : +1)
                .forEach(variation =>
                ppt.push(this.cloneReportItem(item, `${variation.code}-${index}`))
            );
        });

        return ppt;
    }
    private cloneReportItem (item: ReportItem, variationCode: string): ReportItem
    {
        return {
            group                : item.group
        ,   reportType           : item.reportType
        ,   title                : item.title
        ,   file                 : item.file
        ,   selectedVariationCode: variationCode
        ,   variations           : item.variations
        };
    }
}
